import { Profile } from "./Profile";
import { IProfileResponse } from "services/profile";
import { Typography } from "components/Typography";
import styled from "styled-components";
import "react-tabs/style/react-tabs.css";
import { Button } from "./Button";
import { Popular } from "components/Popular";
import { ConfirmModal } from "components/ConfirmModal";
import {
  addFavoriteService,
  deleteFavoriteService,
  favoriteService,
} from "services/favorite";
import { calculatorService, IResultCalculator } from "services/calculator";
import { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { Cart } from "components/Cart";
import { useForm, Controller } from "react-hook-form";
import { Select } from "./Select";
import { materialTypeService } from "services/materialType";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar as EmptyStar } from "@fortawesome/free-regular-svg-icons";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { Loader } from "components/Loader";
import {
  fetchCardItemsService,
  addItemToCardService,
  removeCardItemService,
  clearCartService,
} from "services/cart";
import { getUserId } from "utils/getUserId";
import { getCartId } from "utils/getCartId";
import { Alert } from "./Alert";
import { LabelField } from "./LabelField";
import { CalculatorContext } from "App";

interface IProps {
  profiles: IProfileResponse[] | null;
}
interface IOption {
  label: string;
  value: string;
  default?: boolean;
}

const TabList = styled.div`
  display: grid;
  list-style: none;
  padding: 0;
  width: 100%;
  align-content: center;
  background-color: #fff;
  border-inline: solid 1px #fff;
  gap: 0rem;
  @media (min-width: 800px) {
    grid-template-columns: repeat(3, 1fr);
    position: relative;
    margin: 1.5rem 0;
    gap: 1rem;
    background-color: transparent;
    border-inline: solid 0px #fff;
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;
const Tab = styled.button<{ active: string }>`
  ${({ active }) => ` 
    cursor: pointer;
    transition: 0.2s all ease;
    background-color: transparent; 
    display: flex;
    width: 100%; 
    align-items: center;    
    justify-content: start;
    padding-block: 0.25rem;
    padding-inline: 0;
    text-align: left;
    padding-left: 0rem;
    @media (min-width: 800px) {
      width: 100%;
      background-color: transparent; 
      border-radius: 0;
      box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.1) 0px 2px 4px -2px;
      display:flex;      
      padding: 0.5rem 0.75rem;  
    }
  ${
    active &&
    `
    background-color: #fff; 
    border: 0px solid #262626; 
    @media(min-width:800px){ 
      box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.4) 0px 4px 6px -1px, rgba(0, 0, 0, 0.4) 0px 2px 4px -2px; 
      background-color:#fff;  
    }
  `
  }
`}
`;
const Content = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1rem;
  @media (min-width: 800px) {
    border: solid 1px #aaa;
    border-bottom: 0;
    background-color: #dcdbdb;
  }
`;
const WraperImage = styled.div`
  margin-top: 1rem;
  @media (min-width: 800px) {
    grid-column: span 2 / span 2;
    grid-row: 1/3;
    grid-column-start: 3;
    order: 2;
    align-content: center;
    display: grid;
    padding: 2rem;
  }
`;
const Image = styled.img`
  border-radius: 0;
  border: 1px solid rgba(0, 0, 0, 0.3);
  padding: 0rem;
  margin-top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 268px;
`;
const WrapperSummary = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  margin-top: 0rem;
  @media (min-width: 800px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    background-color: #ebebeb;
    padding: 1.25rem;
    border: solid 1px #aaa;
  }
`;
const GeneralFields = styled.div`
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(2, 1fr);
  @media (min-width: 800px) {
    gap: 1rem;
    padding: 1.25rem;
    padding-bottom: 0;
    background-color: #c0c0c0;
    grid-column: span 2 / span 2;
  }
`;
const CalculationFields = styled.div`
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(2, 1fr);
  @media (min-width: 800px) {
    gap: 1rem;
    display: grid;
    padding: 1.25rem;
    background-color: #c0c0c0;
  }
`;

const Form = styled.form`
  display: grid;
  @media (min-width: 800px) {
    grid-template-columns: 1fr;
  }
`;
const StyledButton = styled(Button)`
  margin-bottom: 10px;
  cursor: pointer;
`;

export const Profiles = ({ profiles }: IProps) => {
  const [profileType, setProfileType] = useState<string | null>(null);
  const [resultCalculator, setResultCalculator] = useState<IResultCalculator>();
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [inputValues, setInputValues] = useState<Record<string, number>>({});
  const [isFavorite, setIsFavorite] = useState(false); // Track whether the item is in favorites or not
  const [isLoading, setIsLoading] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [materials, setMaterials] = useState<IOption[]>([]);
  const [densities, setDensities] = useState<IOption[]>([]);
  const [favorites, setFavorites] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [removedItemId, setRemovedItemId] = useState("");
  const [isOpenClearModalm, setIsOpenClearModal] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [itemAddedToCart, setItemAddedToCart] = useState(false);
  // const [selectedDensity, setSelectedDensity] = useState("");
  const [incorrectInputValues, setIncorrectInputValues] = useState(false);
  const [isAddingToFavorite, setIsAddingToFavorite] = useState(false);
  const [foundFavorite, setFoundFavorite] = useState<any>();

  const userId = getUserId();
  const cartId = getCartId();

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    control,
    setValue,
    watch,
    trigger,
  } = useForm();

  const { a, b, d, L, h, w, density, quantity } = watch();

  useEffect(() => {
    const firstProfile = profiles[0]?.profileType;
    if (firstProfile) setProfileType(firstProfile);
  }, [profiles]);

  const generateDefaultValue = () => {
    const defualtValues = profiles.map((profile) => {
      if (profileType === profile.profileType) {
        return profile.calculationFields.map((item) => ({
          [item.shortName]: item.defautValue,
        }));
      }
    });
    const defaultValueObject = defualtValues.find((item) => item);
    if (defaultValueObject) {
      defaultValueObject.map((item) => {
        for (const [key, value] of Object.entries(item)) {
          setValue(key, value);
          reset({ [key]: value });
        }
      });
    }
  };

  useEffect(() => {
    generateDefaultValue();
  }, [profiles, profileType]);

  const handleChangeProfileType = (data: string) => {
    reset();
    if (profileType === data) {
      return;
    }

    setValue("quantity", 1);

    setIsFavorite(false);
    setIsAddingToFavorite(false);
    setProfileType(data);

    // const defaultDensity = densities.find((density) => density.default)?.value;
    // if (defaultDensity) {
    //   setValue("density", defaultDensity);
    // }

    setResultCalculator({
      ...resultCalculator,
      weight: {
        additionPost: false,
        additionPre: false,
        factor: false,
        label: "",
        symbol: "",
        value: 0,
      },
      length: {
        additionPost: false,
        additionPre: false,
        factor: 0,
        label: "",
        symbol: "",
        value: 0,
      },
    });
  };

  const handleSelect = (val) => {
    setSelectedMaterial(val);
    // setSelectedDensity("");
  };

  const onsubmit = async (data: any) => {
    const valuesOfObject = Object.values(data);

    if (valuesOfObject.length <= 1) return;
    if (valuesOfObject.includes(undefined)) return;
    if (valuesOfObject.includes("")) return;
    if (!density) return;

    setIsLoading(true);
    const measure = [];
    for (const [key, value] of Object.entries(data)) {
      if (value) {
        measure.push({ name: key, value: Number(value) });
      }
    }

    const removedQuantity = measure.filter(
      (item) => item.name !== "quantity" && item.name !== "density"
    );

    const payload = {
      profileType,
      measure: removedQuantity,
      density,
      quantity: data.quantity ? Number(data.quantity) : 1,
    };
    calculateDataSubmit(payload);
  };

  const calculateDataSubmit = async (payload: any) => {
    const response = await calculatorService(payload);
    if (response.status === 200) {
      setIsLoading(false);
      setResultCalculator(response.data.responseData);
      // Check if the calculated weight is negative
      if (response.data.responseData.weight.value < 0) {
        setIncorrectInputValues(true);
      } else {
        setIncorrectInputValues(false);
      }
      return;
    }
  };

  useEffect(() => {
    const timeOutId = setTimeout(() => {
      onsubmit(watch());
    }, 500);
    return () => clearTimeout(timeOutId);
  }, [a, b, h, d, L, w, density, quantity, inputValues]); // Add inputValues to the dependency array

  const fetchDataFromFavoriteList = async (event, data) => {
    event.stopPropagation();

    // Check if the picked profile is in the favorites array
    const isProfileInFavorites = favorites.some((favorite) => {
      const favoriteFilledFields = favorite.filledFields;
      const dataFilledFields = data.filledFields;

      // Compare the count of shortName's and values
      if (favoriteFilledFields.length !== dataFilledFields.length) {
        return false;
      }

      // Compare each item's shortName and value
      for (let i = 0; i < favoriteFilledFields.length; i++) {
        if (
          favoriteFilledFields[i].shortName !== dataFilledFields[i].shortName ||
          favoriteFilledFields[i].value !== dataFilledFields[i].value
        ) {
          return false;
        }
      }

      return true;
    });

    setIsFavorite(isProfileInFavorites);

    handleChangeProfileType(data.profile.name);

    const payloadFields = {};
    const newInputValues = {};

    for (const val of data.filledFields) {
      payloadFields[val.shortName] = val.value;
      newInputValues[val.shortName] = val.value;
    }

    setProfileType(data.profile.profileType);
    setRemovedItemId(data.id);

    setInputValues((prevInputValues) => ({
      ...prevInputValues,
      ...newInputValues,
      density:
        data.filledFields.find((item) => item.shortName === "density")?.value ||
        "",
    }));

    // Trigger validation after setting input values
    await trigger();

    const measure = data.filledFields.map((item) => ({
      name: item.shortName,
      value: item.value,
    }));

    const payload = {
      profileType,
      measure,
      density: densities.find((item) => item.default).value,
      quantity: data.quantity ? Number(data.quantity) : 1,
    };

    // Wait for validation to complete and then submit the form
    calculateDataSubmit(payload);
    setTimeout(() => {
      reset({ ...payloadFields, quantity: 1 });
    });
  };

  useEffect(() => {
    handleInputChange();
  }, [a, b, h, d, L, w, density, quantity]);

  const fetchFavoriteProfiles = async () => {
    if (!userId) return;
    const response = await favoriteService(userId);
    setFavorites(response.data.items);
  };

  const fetchMaterialType = () => {
    materialTypeService().then((response) => {
      if (response.status === 200) {
        const option = response.data.map((item) => {
          if (selectedMaterial === item.slug) {
            const densitieOptions = [];
            item.densities.map((densitie) => {
              densitieOptions.push({
                value: densitie.density,
                label: densitie.density,
                default: densitie.default,
              });
            });
            setDensities(densitieOptions);
          }

          return {
            label: item.name,
            value: item.slug,
            default: item.default,
          };
        });

        setMaterials(option);
      }
    });
  };

  const addToFavorite = async () => {
    const getValue = getValues();
    const filledFields = [];
    profiles.map((profile) => {
      if (profileType === profile.profileType) {
        profile.calculationFields.map((field) => {
          for (const value of Object.keys(getValue)) {
            if (field.shortName === value) {
              filledFields.push({
                name: field.name,
                shortName: value,
                value: getValue[value],
              });
            }
          }
        });
      }
    });

    const foundNullableValue = filledFields.find((item) => !item.value);

    if (foundNullableValue) return;

    const favoriteItem = favorites.find((item) => {
      const favoriteFilledFields = item.filledFields;

      // Compare the count of shortName's and values
      if (favoriteFilledFields.length !== filledFields.length) {
        return false;
      }

      // Compare each item's shortName and value
      for (let i = 0; i < favoriteFilledFields.length; i++) {
        if (
          favoriteFilledFields[i].shortName !== filledFields[i].shortName ||
          favoriteFilledFields[i].value !== filledFields[i].value
        ) {
          return false;
        }
      }

      return true;
    });

    if (favoriteItem) {
      // Remove from favorites if already in favorites
      handleRemoveFromFavourite(favoriteItem.id);
    } else {
      // Add to favorites if not in favorites
      const payload = {
        profileType,
        filledFields,
        popularId: "",
      };
      const response = await addFavoriteService(userId, payload);
      if (response.status === 200) {
        const resId = response.data.responseData.id;
        setFoundFavorite(resId);
      }
      fetchFavoriteProfiles();
      setIsFavorite(true);
    }

    openAlert();
  };

  useEffect(() => {
    // console.log(foundFavorite);
  }, [foundFavorite]);

  const handleRemoveFromFavourite = async (id) => {
    await deleteFavoriteService(id);
    fetchFavoriteProfiles();
    setIsFavorite(false);
  };

  const handleAddFavoriteItem = async (isFav: boolean) => {
    setIsAddingToFavorite(true);
    if (!isFav) {
      await addToFavorite();
    }
    setIsAddingToFavorite(false);
  };

  const handleRemoveFavoriteItem = async () => {
    setIsAddingToFavorite(true);
    await handleRemoveFromFavourite(foundFavorite);
    setIsAddingToFavorite(false);
  };

  const context = useContext(CalculatorContext);

  const { translations, handleItemCount } = context!;

  const fetchCardItems = async () => {
    const response = await fetchCardItemsService(getCartId());
    setCartItems(response.data.items);
    handleItemCount(response.data.items.length);
  };

  const addItemToCard = async () => {
    const getValue = getValues();
    const filledFields = [];
    profiles.map((profile) => {
      if (profileType === profile.profileType) {
        profile.calculationFields.map((field) => {
          for (const value of Object.keys(getValue)) {
            if (field.shortName === value) {
              filledFields.push({
                name: field.name,
                shortName: value,
                value: getValue[value],
              });
            }
          }
        });
      }
    });

    const foundNullableValue = filledFields.find((item) => !item.value);

    if (foundNullableValue) return;

    const payload = {
      profileType,
      quantity,
      density,
      filledFields,
    };

    const response = await addItemToCardService(cartId, payload);
    if (response.status === 200) {
      setItemAddedToCart(true); // Ustawienie stanu na true po dodaniu do koszyka
      fetchCardItems();
    }
  };

  const handleRemoveItem = async () => {
    const response = await removeCardItemService(removedItemId);
    if (response.status === 200) {
      setIsOpen(false);
      fetchCardItems();
    }
  };

  const handleClearCart = async () => {
    const response = await clearCartService(getCartId());
    if (response.status === 200) {
      setIsOpenClearModal(false);
      fetchCardItems();
    }
  };

  const handleOpenClearConfirmModal = (id: string) => {
    if (cartItems.length === 0) return;

    setIsOpenClearModal(true);
    setRemovedItemId(id);
  };

  const handleOpenConfirmModal = (id: string) => {
    setIsOpen(true);
    setRemovedItemId(id);
  };

  useEffect(() => {
    fetchMaterialType();
  }, [selectedMaterial]);

  useEffect(() => {
    fetchCardItems();
  }, []);

  useEffect(() => {
    fetchFavoriteProfiles();
    reset({ quantity: 1 });
  }, []);

  useEffect(() => {
    const mainContainer = document.querySelector("#root main");
    const divsWithSpecialClass =
      mainContainer.querySelectorAll("#root>main>div");
    if (divsWithSpecialClass.length === 7) {
      mainContainer.classList.add("special-margin");
    } else if (divsWithSpecialClass.length !== 7) {
      mainContainer.classList.remove("special-margin");
    }
  });
  const handleInputChange = () => {
    const inputValues: Record<string, number> = {}; // Store input values for each field name

    // Collect input values for each field name
    document.querySelectorAll("input[name]").forEach((input) => {
      const fieldName = input.getAttribute("name");
      const inputValue = parseFloat((input as HTMLInputElement).value); // Type assertion
      if (!isNaN(inputValue)) {
        inputValues[fieldName] = inputValue;
      }
    });

    // Check if there is a matching favorite for all input values
    const isMatchingFavorite = favorites.some((favorite) => {
      return favorite.filledFields.every((field) => {
        if (
          inputValues[field.shortName] !== undefined &&
          parseFloat(field.value) === inputValues[field.shortName]
        ) {
          setFoundFavorite(favorite);
          return true;
        } else {
          return false;
        }
      });
    });
    setIsFavorite(isMatchingFavorite);
  };

  const openAlert = useCallback(() => {
    setAlertOpen(true);
  }, []);

  const closeAlert = () => {
    setAlertOpen(false);
  };

  useEffect(() => {
    if (alertOpen) {
      const timer = setTimeout(() => {
        closeAlert();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alertOpen]);

  useEffect(() => {
    if (itemAddedToCart) {
      // reset(); // Zresetowanie pól formularza
      setResultCalculator(undefined); // Wyzerowanie resultCalculator
      setItemAddedToCart(false); // Ustawienie stanu na false
    }
  }, [itemAddedToCart]);

  const activeProfile = profiles.find(
    (item) => item.profileType === profileType
  );

  useEffect(() => {
    const defaultDensity = densities.find((density) => density.default)?.value;
    if (defaultDensity) {
      setValue("density", defaultDensity);
    }
  }, [profileType]);

  useEffect(() => {
    const mainContainer = document.querySelector("#root main");
    const divsWithSpecialClass =
      mainContainer.querySelectorAll("#root>main>div");

    if (divsWithSpecialClass.length === 7) {
      mainContainer.classList.add("special-margin");
    } else if (divsWithSpecialClass.length !== 7) {
      mainContainer.classList.remove("special-margin");
    }
  });

  const handleKeyPress = (e) => {
    if (e.key === "-" || e.key === "–") {
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    const clipboardData = e.clipboardData || window.Clipboard;
    const pastedData = clipboardData.getData("text");
    if (pastedData.includes("-")) {
      e.preventDefault();
    }
  };

  return (
    <>
      {translations && (
        <>
          <TabList className={profileType ? "aside-open" : ""}>
            {profiles.map((item) => (
              <Tab
                active={item.profileType === profileType ? "active" : ""}
                key={item.id}
                onClick={() => handleChangeProfileType(item.profileType)}
              >
                <Profile profile={item} />
              </Tab>
            ))}
          </TabList>

          {profileType && (
            <>
              <div>
                <p className="element-header">
                  {
                    profiles.find((item) => item.profileType === profileType)
                      ?.name
                  }
                </p>
              </div>
              {profiles.map((profile) => (
                <Fragment key={profile.id}>
                  {profile.profileType === profileType && (
                    <Content>
                      <>
                        <Form onSubmit={handleSubmit(onsubmit)}>
                          <GeneralFields>
                            <div>
                              <Select
                                title={`${translations["material-type"]}`}
                                selectedProvider={
                                  materials.find((profile) => profile.default)
                                    ?.value
                                }
                                id="material-type"
                                options={materials}
                                onSelected={handleSelect}
                              />
                            </div>
                            <div>
                              <Controller
                                control={control}
                                name="density"
                                render={({ field: { onChange, value } }) => (
                                  <Select
                                    title={`${translations["calc-density"]}`}
                                    selectedProvider={
                                      densities.find(
                                        (profile) => profile.default
                                      )?.label
                                    }
                                    options={densities}
                                    onSelected={onChange}
                                  />
                                )}
                              />
                            </div>
                          </GeneralFields>

                          <CalculationFields>
                            {profile.calculationFields.map((fieldItem) => {
                              return (
                                <div key={fieldItem.shortName}>
                                  <LabelField
                                    title={fieldItem.name}
                                    type="number"
                                    {...register(`${fieldItem.shortName}`, {
                                      pattern: /^[0-9]*$/,
                                    })}
                                    min={1}
                                    aria-label="test"
                                    onKeyDown={handleKeyPress}
                                    onPaste={handlePaste}
                                  />
                                </div>
                              );
                            })}
                            <LabelField
                              title={`${translations["count-of-items"]}`}
                              type="number"
                              min={1}
                              {...register("quantity")}
                            />
                          </CalculationFields>

                          <WraperImage>
                            <Image src={profile.imageUrl} />
                          </WraperImage>
                        </Form>
                      </>
                    </Content>
                  )}
                </Fragment>
              ))}
            </>
          )}

          <WrapperSummary className="summary-container">
            <Typography variant="h2" className="mb-3">
              {translations["calc-result"]}
            </Typography>
            <div>
              {isLoading && <Loader />}

              {incorrectInputValues ? (
                <>
                  <p>{translations["total-weight-of-elements"]}</p>
                  <Typography variant="p" color="#c42f19">
                    {translations["provided-values-are-incorrect"]}
                  </Typography>
                </>
              ) : (
                <>
                  <p>{translations["total-weight-of-elements"]}</p>
                  <Typography
                    variant="h2"
                    color="#003057"
                    className="header-wrap"
                  >
                    {(isNaN(resultCalculator?.weight?.value)
                      ? 0
                      : resultCalculator?.weight?.value / 1000
                    ).toFixed(2)}{" "}
                    kg
                  </Typography>
                </>
              )}
            </div>
            <div>
              <p>{translations["total-running-meters"]}</p>
              <Typography variant="h2" color="#003057" className="header-wrap">
                {(isNaN(resultCalculator?.length.value)
                  ? 0
                  : resultCalculator?.length.value / 1000
                ).toFixed(2)}{" "}
                m
              </Typography>
            </div>
            {resultCalculator?.weight?.value !== 0 &&
            resultCalculator?.length.value !== 0 &&
            !Object.values(watch()).includes("") ? (
              <div className="container-favorite">
                <StyledButton onClick={addItemToCard} className="btn-add-item">
                  {translations["add-to-list"]}
                </StyledButton>

                {isAddingToFavorite ? (
                  // Show loader while adding to favorite
                  <Loader />
                ) : (
                  // Show appropriate text and icon based on isFavorite state
                  <>
                    {isFavorite ? (
                      <>
                        <button
                          className="btn-favorite"
                          onClick={handleRemoveFavoriteItem}
                        >
                          {translations["remove-from-fav"]}{" "}
                          <FontAwesomeIcon icon={faStar} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="btn-favorite"
                          onClick={() => handleAddFavoriteItem(isFavorite)}
                          disabled={isAddingToFavorite}
                        >
                          {translations["save-as-fav"]}{" "}
                          <FontAwesomeIcon icon={EmptyStar} />
                        </button>
                      </>
                    )}
                  </>
                )}
                <Alert isOpen={alertOpen} onClose={closeAlert}>
                  <p>
                    {isFavorite
                      ? `${translations["added-to-fav"]}`
                      : `${translations["removed-to-fav"]}`}
                  </p>
                </Alert>
              </div>
            ) : (
              <div className="container-favorite">
                <StyledButton disabled className="btn-add-item">
                  {translations["add-to-list"]}
                </StyledButton>
              </div>
            )}
          </WrapperSummary>

          <Popular
            profiles={profiles} // Pass the profiles array
            profileType={profileType}
            favorites={favorites.filter(
              (item) => item.profile.profileType === activeProfile?.profileType
            )}
            fetchFavoriteProfiles={fetchFavoriteProfiles}
            setIsFavorite={setIsFavorite}
            handleClick={fetchDataFromFavoriteList}
          />

          <Cart
            items={cartItems}
            handleRemove={handleOpenConfirmModal}
            handleClear={handleOpenClearConfirmModal}
          />

          <ConfirmModal
            isOpen={isOpen}
            handleClose={() => setIsOpen(false)}
            handleSubmit={handleRemoveItem}
            modalText={`${translations["remove-item-from-basket"]}`}
          />
          <ConfirmModal
            isOpen={isOpenClearModalm}
            handleClose={() => setIsOpenClearModal(false)}
            handleSubmit={handleClearCart}
            modalText={`${translations["remove-all-items-from-basket"]}`}
          />
        </>
      )}
    </>
  );
};
